import { AriaDialogProps } from '@react-types/dialog';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FocusScope, OverlayContainer, useButton, useDialog, useModal, useOverlay, usePreventScroll } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';

import { IconArrowLeft, IconNavigationCloseNormal } from '../Icons';

import { Button, ButtonProps } from '../Button/Button';
import { ModalUnderlay } from './ModalUnderlay/ModalUnderlay';
import { OpenTransition } from './OpenTransition/OpenTransition';

import './Modal.scss';

export interface OverlayProps {
  isOpen?: boolean;
  onClose?: () => void;
  isDismissable?: boolean;
  shouldCloseOnBlur?: boolean;
  isKeyboardDismissDisabled?: boolean;
  shouldCloseOnInteractOutside?: (element: HTMLElement) => boolean;
}

interface ModalButtonProps extends Omit<ButtonProps, 'children'> {
  label: string;
  closeModalOnClick: boolean;
  onClick?: () => void;
  closeFn?: () => void;
}

export function ModalButton({
  label = null,
  closeModalOnClick,
  onClick,
  closeFn,
  variant = 'primary',
  ...rest
}: ModalButtonProps): React.ReactElement {
  if (!label) {
    return null;
  }

  return (
    <Button
      variant={variant}
      onClick={(): void => {
        typeof onClick === 'function' && onClick();
        if (closeModalOnClick && typeof closeFn === 'function') {
          closeFn();
        }
      }}
      {...rest}
    >
      {label}
    </Button>
  );
}

export interface ModalWindowProps extends AriaDialogProps {
  isOpen?: boolean;
  title?: string;
  isDismissable?: boolean;
  intro?: React.ReactNode;
  children: React.ReactNode;
  closeButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  back?: boolean;
  mobileFull?: boolean;
  onClose?: () => void;
  primaryButton?: ModalButtonProps;
  secondaryButton?: ModalButtonProps;
  onBack?: (e: React.MouseEvent) => void;
  compactView?: boolean;
  className: string;
  full?: boolean;

  buttonsPosition?: 'start' | 'end' | 'center';
}

function _ModalWindow(
  {
    children,
    isOpen = false,
    title,
    intro,
    back,
    mobileFull = false,
    closeButtonProps,
    primaryButton = null,
    secondaryButton = null,
    isDismissable = true,
    full = false,
    compactView = false,
    className,
    buttonsPosition = 'end',
    onBack,
    ...otherProps
  }: ModalWindowProps,
  ref: React.RefObject<HTMLDivElement>
): React.ReactElement {
  const { modalProps } = useModal();

  // this section is used to adjust the modal size accordingly to iframe content.
  // the best way to know the content of an iframe is to post a message from the iframe to the parent window
  // we react on the message adjusting the height of the modal
  const [modalStyle, setModalStyle] = useState({});
  const handleIframeSize = useCallback((event) => {
    try {
      const height = JSON.parse(event.data)?.height || null;
      if (height !== null && typeof height !== 'undefined') {
        setModalStyle({ height: `${height + 10}px`, overflow: 'hidden' });
      }
    } catch {
      return;
    }
  }, []);
  useEffect(() => {
    window.addEventListener('message', handleIframeSize, false);
    return function removeEventListener(): void {
      window.removeEventListener('message', handleIframeSize, false);
    };
  }, [handleIframeSize]);

  // Get props for the dialog and its title
  // enforce a role of type dialog if the modal is dismissable or alertdialog if it's not
  const { dialogProps, titleProps } = useDialog({ ...otherProps, role: isDismissable ? 'dialog' : 'alertdialog' }, ref);

  const integratedButtons = !!primaryButton || !!secondaryButton;

  const wrapsIframe = children && React.isValidElement(children) && children.type === 'iframe';

  const modalClasses = classNames('modal', {
    'is-open': isOpen,
    'mobile-full': mobileFull,
    iframe: wrapsIframe,
    [`${className}`]: className,
  });

  return (
    <div {...dialogProps} {...modalProps} {...otherProps} className={modalClasses} ref={ref}>
      {(isDismissable || back || title) &&
        !compactView && ( // show the header when there is something to show inside
          <div className={classNames('heading', { [`${className}__heading`]: className, noBorder: !!intro })}>
            <div className={classNames('back', { [`${className}__back`]: className })}>
              {back && (
                <button onClick={onBack}>
                  <IconArrowLeft />
                </button>
              )}
            </div>
            <h3 {...titleProps} className={classNames('title', { [`${className}__title`]: className })}>
              {title}
            </h3>
            <div className={classNames('close', { [`${className}__close`]: className })}>
              {isDismissable && (
                <button {...closeButtonProps}>
                  <IconNavigationCloseNormal />
                </button>
              )}
            </div>
          </div>
        )}
      {intro && <div className={classNames('intro', { [`${className}__intro`]: className })}>{intro}</div>}
      {children && (
        <div
          className={classNames('content', {
            wide: !intro,
            iframe: wrapsIframe,
            [`${className}__content`]: className,
            [`${className}__content--wide`]: className && !intro,
            [`content--full`]: full,
          })}
          style={modalStyle}
        >
          {children}
          {integratedButtons && (
            <div
              className={classNames('buttonsWrapper', {
                [`${className}__buttonsWrapper`]: className,
                [`buttonsWrapper--${buttonsPosition}`]: buttonsPosition,
              })}
            >
              <ModalButton {...secondaryButton} />
              <ModalButton {...primaryButton} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const ModalWindow = React.forwardRef(_ModalWindow);

export interface ModalProps extends OverlayProps, AriaDialogProps, ModalWindowProps {
  underlay?: string;
}
export function Modal({
  isOpen = false,
  title = null,
  intro = null,
  back = false,
  mobileFull = false,
  primaryButton = null,
  secondaryButton = null,
  isDismissable = true,
  ...otherProps
}: ModalProps): React.ReactElement {
  // Handle interacting outside the dialog and pressing
  // the Escape key to close the modal.
  const ref = useRef();

  const state = useOverlayTriggerState({});

  // when isOpen prop changes, reflect it's state to internal state
  useEffect(() => {
    if (!isOpen) {
      state.close();
    } else {
      state.open();
    }
  }, [isOpen, state]);

  const closeFn = otherProps.onClose
    ? () => {
        state.close();
        otherProps.onClose();
      }
    : state.close;

  const props: ModalProps = {
    ...otherProps,
    isOpen,
    title,
    intro,
    back,
    mobileFull,
    primaryButton: primaryButton ? { ...primaryButton, closeFn } : null,
    secondaryButton: secondaryButton ? { ...secondaryButton, closeFn } : null,
    isDismissable,
  };

  const { overlayProps } = useOverlay(
    {
      isOpen,
      isDismissable,
      isKeyboardDismissDisabled: !isDismissable,
      onClose: () => {
        closeFn();
      },
    },
    ref
  );

  const { buttonProps: closeButtonProps } = useButton(
    {
      onPress: () => {
        closeFn();
      },
    },
    ref
  );

  // Prevent scrolling while the modal is open, and hide content
  // outside the modal from screen readers.
  usePreventScroll({ isDisabled: !state.isOpen });

  if (!state.isOpen) {
    return null;
  }

  const focusProps = {
    contain: false, // if contain true, can't move inside iframes
    restoreFocus: true,
    autoFocus: true,
  };

  return (
    <OverlayContainer>
      <div {...overlayProps} className={classNames('modalWrapper', { [`${props.className}__modalWrapper`]: props.className })}>
        <FocusScope {...focusProps}>
          <OpenTransition in={state.isOpen} appear>
            <ModalUnderlay className={props.className} />
            <ModalWindow {...props} isOpen={state.isOpen} closeButtonProps={closeButtonProps} ref={ref} />
          </OpenTransition>
        </FocusScope>
      </div>
    </OverlayContainer>
  );
}
